import { useAuth } from "../contexts/AuthContext";
import { Permission } from "../types/auth";

export interface PermissionCheck {
  service: string;
  resource: string;
  action: string;
}

export const usePermissions = () => {
  const { user } = useAuth();

  const isSuperAdmin = (): boolean => {
    return (
      user?.roles?.some(
        (role) => role.name === "Super Admin" && role.isActive !== false
      ) || false
    );
  };

  const getUserPermissions = (): Permission[] => {
    if (!user?.roles) return [];

    const permissions: Permission[] = [];
    const seen = new Set<string>();

    user.roles.forEach((role) => {
      if (role.isActive === false || !role.permissions) return;

      role.permissions.forEach((rolePermission) => {
        let permission: Permission | null = null;

        if ("permission" in rolePermission && rolePermission.permission) {
          permission = rolePermission.permission;
        } else if (rolePermission) {
          permission = rolePermission as Permission;
        }

        if (!permission) return;

        if (permission.isActive !== false && !seen.has(permission.id)) {
          permissions.push(permission);
          seen.add(permission.id);
        }
      });
    });

    return permissions;
  };

  const hasPermission = (
    service: string,
    resource: string,
    action: string
  ): boolean => {
    const permissions = getUserPermissions();

    return permissions.some(
      (p) =>
        p.service === service && p.resource === resource && p.action === action
    );
  };

  const hasAnyPermission = (service: string, resource: string): boolean => {
    const permissions = getUserPermissions();

    return permissions.some(
      (p) => p.service === service && p.resource === resource
    );
  };

  const hasReadPermission = (service: string, resource: string): boolean => {
    return hasPermission(service, resource, "read");
  };

  const hasCreatePermission = (service: string, resource: string): boolean => {
    return hasPermission(service, resource, "create");
  };

  const hasUpdatePermission = (service: string, resource: string): boolean => {
    return hasPermission(service, resource, "update");
  };

  const hasDeletePermission = (service: string, resource: string): boolean => {
    return hasPermission(service, resource, "delete");
  };

  const hasManagePermission = (service: string, resource: string): boolean => {
    return hasPermission(service, resource, "manage");
  };

  const hasPermissions = (permissions: PermissionCheck[]): boolean => {
    return permissions.every((p) =>
      hasPermission(p.service, p.resource, p.action)
    );
  };

  const hasAnyOfPermissions = (permissions: PermissionCheck[]): boolean => {
    return permissions.some((p) =>
      hasPermission(p.service, p.resource, p.action)
    );
  };

  const getRequiredPermission = (
    service: string,
    resource: string,
    action: string
  ): string => {
    return `${service}:${resource}:${action}`;
  };

  return {
    getUserPermissions,
    hasPermission,
    hasAnyPermission,
    hasReadPermission,
    hasCreatePermission,
    hasUpdatePermission,
    hasDeletePermission,
    hasManagePermission,
    hasPermissions,
    hasAnyOfPermissions,
    isSuperAdmin,
    getRequiredPermission,
  };
};
